import {
  faDownload,
  faFileCsv,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type FormEvent, useEffect, useRef, useState } from "react";
import React from "react";
import { Link, Navigate } from "react-router-dom";

import { api, ApiClientError } from "../api";
import { useAuth } from "../auth";
import { localDateInputValue } from "../date";
import { detectImportKind, type ImportKind } from "../import-file";
import { importProgressMessage, type ImportResult } from "../import-status";
import { startPolling } from "../polling";

const template =
  "agenda_number,description,source_url,source_label,rounds\n4A,Develop the adopted position,https://example.gov/agenda,Agenda item 4A,Initial hearing|Final hearing\n";

export function ImportPage() {
  const { user, loading } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [fileKind, setFileKind] = useState<ImportKind | null>(null);
  const [job, setJob] = useState<ImportResult | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const fileSelection = useRef(0);
  const pollingJobId = job?.id ?? null;
  const pollingFinished =
    job?.status === "completed" || job?.status === "failed";
  useEffect(() => {
    if (!pollingJobId || pollingFinished) return;
    return startPolling({
      request: () => api<ImportResult>(`/api/imports/${pollingJobId}`),
      onValue: (updated) => {
        setError("");
        setJob(updated);
      },
      onError: () =>
        setError(
          "Import status could not be refreshed. Retrying automatically.",
        ),
      shouldContinue: (updated) =>
        updated.status !== "completed" && updated.status !== "failed",
      delayMs: 2_000,
    });
  }, [pollingFinished, pollingJobId]);
  if (loading && !user)
    return (
      <div className="page-section">
        <div className="container loading-block" role="status">
          Checking access…
        </div>
      </div>
    );
  if (!user)
    return <Navigate to="/sign-in" replace state={{ from: "/import" }} />;
  if (
    !user.isSystemAdmin &&
    user.account.role !== "owner" &&
    user.account.role !== "manager"
  )
    return (
      <div className="page-section">
        <div className="container empty-state">
          <h1>Manager access required</h1>
          <p>An owner or manager can import an organization agenda.</p>
          <Link className="button" to="/library?scope=mine">
            Return to your guides
          </Link>
        </div>
      </div>
    );
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const form = new FormData(event.currentTarget);
      form.set("file", file);
      const created = await api<ImportResult>(
        fileKind === "pdf" ? "/api/imports/guides/pdf" : "/api/imports/guides",
        { method: "POST", body: form },
      );
      setJob({
        ...created,
        rowCount: 0,
        importedCount: 0,
        errorCount: 0,
        errors: [],
      });
    } catch (caught) {
      setError(
        caught instanceof ApiClientError
          ? caught.message
          : "Unable to start import.",
      );
    } finally {
      setBusy(false);
    }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([template], { type: "text/csv" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "stancekit-guide-items.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="page-section">
      <div className="container form-container">
        <div className="page-heading">
          <div>
            <p className="kicker">Bulk agenda</p>
            <h1>Import an agenda</h1>
          </div>
          <button className="button button-secondary" onClick={download}>
            <FontAwesomeIcon icon={faDownload} /> CSV template
          </button>
        </div>
        <form
          className="panel form-grid"
          encType="multipart/form-data"
          onSubmit={(event) => void submit(event)}
        >
          <label className="field-span">
            Guide title
            <input name="title" required minLength={3} />
          </label>
          <label className="field-span">
            Guide description
            <textarea name="description" required rows={4} />
          </label>
          <label>
            Guide date
            <input
              name="guideDate"
              type="date"
              required
              defaultValue={localDateInputValue()}
            />
          </label>
          <label>
            Visibility
            <select name="visibility" defaultValue="private">
              <option value="private">Private</option>
              <option value="public">Public when published</option>
            </select>
          </label>
          <label>
            Hearing structure
            <select name="hearingType" defaultValue="flexible">
              <option value="simple">Single round</option>
              <option value="two_vote">Two rounds</option>
              <option value="three_vote">Three rounds</option>
              <option value="four_vote">Four rounds</option>
              <option value="flexible">Flexible</option>
            </select>
          </label>
          <div className="upload-panel field-span">
            <FontAwesomeIcon
              icon={fileKind === "pdf" ? faFilePdf : faFileCsv}
            />
            <h2>Choose a CSV or PDF</h2>
            <input
              name="file"
              aria-label="CSV or PDF file"
              type="file"
              accept=".csv,.pdf,text/csv,application/pdf"
              required
              onChange={(event) => {
                const selection = (fileSelection.current += 1);
                const selected = event.target.files?.[0] ?? null;
                const input = event.currentTarget;
                if (!selected) {
                  setFile(null);
                  setFileKind(null);
                  return;
                }
                void detectImportKind(selected)
                  .then((kind) => {
                    if (fileSelection.current !== selection) return;
                    if (kind === "pdf" && selected.size > 100 * 1024 * 1024)
                      throw new Error("PDF files cannot exceed 100 MB.");
                    setError("");
                    setFile(selected);
                    setFileKind(kind);
                  })
                  .catch((caught: unknown) => {
                    if (fileSelection.current !== selection) return;
                    setFile(null);
                    setFileKind(null);
                    setError(
                      caught instanceof Error
                        ? caught.message
                        : "Choose a CSV or PDF file.",
                    );
                    input.value = "";
                  });
              }}
            />
            {file ? (
              <>
                <p className="selected-file">
                  {file.name} · {fileKind?.toUpperCase()}
                </p>
                <p className="upload-note">
                  {fileKind === "pdf"
                    ? "Creates agenda items, attaches source pages, and adds labeled AI-assisted descriptions. Positions are never generated."
                    : "Imports each row as written. Positions are never generated."}
                </p>
              </>
            ) : null}
          </div>
          {error ? (
            <div className="alert alert-error field-span" role="alert">
              {error}
            </div>
          ) : null}
          <div className="form-actions field-span">
            <Link className="button button-ghost" to="/guides/new">
              Manual entry
            </Link>
            <button className="button" disabled={busy || !file || !fileKind}>
              {busy ? "Uploading…" : "Import"}
            </button>
          </div>
        </form>
        {job ? (
          <section className="panel import-result" aria-live="polite">
            <h2>
              {job.status === "pending"
                ? "Import queued"
                : job.status === "processing"
                  ? "Importing agenda"
                  : job.status === "completed"
                    ? "Agenda imported"
                    : "Import failed"}
            </h2>
            <p>{importProgressMessage(job)}</p>
            {job.status === "failed" && job.errors.length ? (
              <div className="import-error-summary" role="alert">
                <h3>What stopped the import</h3>
                <ul>
                  {job.errors.slice(0, 5).map((jobError, index) => (
                    <li key={`${String(jobError.row)}-${String(index)}`}>
                      {jobError.row > 0 ? `Row ${String(jobError.row)}: ` : ""}
                      {jobError.message}
                    </li>
                  ))}
                </ul>
                {job.errors.length > 5 ? (
                  <p>
                    {String(job.errors.length - 5)} more errors are available in
                    the row-error download.
                  </p>
                ) : null}
              </div>
            ) : null}
            {job.status === "completed" && job.cacheHit ? (
              <p>Reused a verified match from the document cache.</p>
            ) : null}
            {job.status === "completed" && job.accountSlug && job.guideCode ? (
              <Link
                className="button"
                to={`/guides/${job.accountSlug}/${job.guideCode}`}
              >
                Open guide
              </Link>
            ) : null}
            {job.errorCount ? (
              <a
                className="button button-secondary"
                href={`/api/imports/${job.id}/errors.csv`}
              >
                <FontAwesomeIcon icon={faDownload} /> Download row errors
              </a>
            ) : null}
          </section>
        ) : null}
      </div>
    </div>
  );
}
